import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../common/Modal/Modal';
import { useScrollPagination } from '../../../hooks/useScrollPagination';
import type { PageView, UserView } from '../../../types';

interface PageViewsCardProps {
  pageStats: PageView[];
}

interface SelectedPage {
  path: string;
  label: string;
}

const pageMeta = [
  { path: '/', labelKey: 'nav.home', icon: 'home' },
  { path: '/about', labelKey: 'nav.about', icon: 'info' },
  { path: '/services', labelKey: 'nav.services', icon: 'briefcase' },
  { path: '/consultation', labelKey: 'nav.consultation', icon: 'calendar' },
  { path: '/hiring', labelKey: 'nav.hiring', icon: 'users' },
  { path: '/contact', labelKey: 'nav.contact', icon: 'mail' },
];

export function PageViewsCard({ pageStats }: PageViewsCardProps) {
  const { t } = useTranslation();
  const [selectedPage, setSelectedPage] = useState<SelectedPage | null>(null);
  const [displayedCount, setDisplayedCount] = useState(15);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const mergedStats = pageMeta.map((meta) => {
    const dbStat = pageStats.find((s) => s.pagePath === meta.path);
    return {
      path: meta.path,
      label: t(meta.labelKey),
      icon: meta.icon,
      views: dbStat ? dbStat.views : 0,
    };
  });

  const totalViews = mergedStats.reduce((sum, item) => sum + item.views, 0);
  const sortedStats = [...mergedStats].sort((a, b) => b.views - a.views);

  const currentPageStat = pageStats.find((s) => s.pagePath === selectedPage?.path);
  const userViews = currentPageStat?.userViews || [];
  const hasMore = userViews.length > displayedCount;

  const loadMoreViewers = () => {
    if (isLoadingMore) return;
    setIsLoadingMore(true);
    setTimeout(() => {
      setDisplayedCount((prev) => prev + 15);
      setIsLoadingMore(false);
    }, 300);
  };

  useScrollPagination({
    loadMore: loadMoreViewers,
    hasMore,
    isLoading: isLoadingMore,
    containerRef,
    threshold: 40,
  });

  const handleOpenVisitors = (item: SelectedPage) => {
    setSelectedPage(item);
    setDisplayedCount(15);
  };

  const handleCloseVisitors = () => {
    setSelectedPage(null);
  };

  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case 'home':
        return (
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
        );
      case 'info':
        return (
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 16v-4"/>
            <path d="M12 8h.01"/>
          </svg>
        );
      case 'briefcase':
        return (
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 2H9a2 2 0 0 0-2 2v2H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-4V4a2 2 0 0 0-2-2z"/>
            <path d="M7 6h10"/>
          </svg>
        );
      case 'calendar':
        return (
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
        );
      case 'users':
        return (
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        );
      case 'mail':
        return (
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect width="20" height="16" x="2" y="4" rx="2"/>
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="admin-chart-section admin-pageviews-section">
        <div className="admin-chart-header">
          <div>
            <h2 className="admin-chart-title">{t('admin.pageViewsTitle', 'Page Views Statistics')}</h2>
            <p className="admin-chart-subtitle">
              {t('admin.totalPageViews', 'Total views across all public pages')}:{' '}
              <span className="admin-chart-highlight">{totalViews.toLocaleString()}</span>
            </p>
          </div>
        </div>

        <div className="admin-pageviews-list">
          {sortedStats.map((item) => {
            const percent = totalViews > 0 ? (item.views / totalViews) * 100 : 0;
            return (
              <div key={item.path} className="admin-pageview-item">
                <div className="admin-pageview-info">
                  <div className="admin-pageview-name-wrap">
                    <span className={`admin-pageview-icon icon-${item.icon}`}>
                      {renderIcon(item.icon)}
                    </span>
                    <div>
                      <span className="admin-pageview-name">{item.label}</span>
                      <span className="admin-pageview-path">{item.path}</span>
                    </div>
                  </div>
                  <div className="admin-pageview-actions">
                    <span className="admin-pageview-count">
                      {item.views.toLocaleString()} {t('admin.views', 'views')}
                    </span>
                    <button
                      type="button"
                      className="admin-pageview-viewers-btn"
                      onClick={() => handleOpenVisitors({ path: item.path, label: item.label })}
                      title={t('admin.viewVisitors', 'View visitors')}
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="admin-pageview-progress-bg">
                  <div
                    className={`admin-pageview-progress-bar bar-${item.icon}`}
                    style={{ width: `${percent}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Modal
        isOpen={selectedPage !== null}
        onClose={handleCloseVisitors}
        title={
          selectedPage
            ? `${t('admin.visitorsFor', 'Visitors for')} ${selectedPage.label}`
            : t('admin.visitorsFor', 'Visitors')
        }
        className="admin-pageview-visitors-modal"
      >
        {(() => {
          const currentPageStat = pageStats.find((s) => s.pagePath === selectedPage?.path);
          const userViews = currentPageStat?.userViews || [];
          const sortedUserViews = [...userViews].sort((a, b) => b.count - a.count);

          if (sortedUserViews.length === 0) {
            return (
              <div className="admin-pageview-visitors-empty">
                {t('admin.noUserStats', 'No user statistics available.')}
              </div>
            );
          }

          return (
            <div ref={containerRef} className="admin-viewers-list" style={{ maxHeight: '420px', overflowY: 'auto' }}>
              {sortedUserViews.slice(0, displayedCount).map((viewer, index) => {
                const isGuest = viewer.userId === null;
                const name = viewer.viewerName || t('admin.guestUser', 'Guest User');
                const initial = isGuest ? 'G' : name.charAt(0).toUpperCase();

                return (
                  <div key={`${viewer.userId || 'guest'}-${index}`} className="admin-viewer-item">
                    <div className="admin-viewer-info-wrap">
                      <span className={`admin-user-avatar ${isGuest ? 'guest' : ''}`}>
                        {initial}
                      </span>
                      <div className="admin-viewer-details">
                        <span className="admin-viewer-name">{name}</span>
                        {!isGuest && viewer.viewerEmail && (
                          <span className="admin-viewer-email">{viewer.viewerEmail}</span>
                        )}
                      </div>
                    </div>
                    <div className="admin-viewer-count-wrap">
                      <span className="admin-viewer-count">{viewer.count.toLocaleString()}</span>
                      <span className="admin-viewer-count-label">{t('admin.views', 'views')}</span>
                    </div>
                  </div>
                );
              })}
              {isLoadingMore && (
                <div className="admin-pageview-visitors-loading" style={{ padding: '16px 0' }}>
                  <span className="auth-spinner auth-spinner-brand"></span>
                </div>
              )}
            </div>
          );
        })()}
      </Modal>
    </>
  );
}
